/**
 * Collapse Keys — the story track's delivery mechanism (TODO §B).
 *
 * A key is an ACCOUNT-LEVEL RECORD, not an item. `gameState.player.collapseKeys`
 * holds recovered ids; nothing enters an inventory, so a key cannot be sold,
 * crafted, consigned, levied or lost. That shape was chosen deliberately over
 * the `unique` story-relic loot pool:
 *
 *   - The loot route would have meant lowering `itemRarities.unique.minTier`
 *     from 4, which makes the unique leg of getRoomRarityChances() non-zero at
 *     T1-T3 (visible in the raid planner), drags the Relic Resonance Tuner's
 *     selection boost into the story, and puts a balance surface under content
 *     that has no economic value.
 *   - It would ALSO have needed an explicit entry in foundSyndicateCore's
 *     preserved ledger, because a founding rebuilds command and wipes
 *     inventories. Account-level state needs no such entry — the player object
 *     is saved and restored whole and no prestige touches it. The invariant is
 *     enforced by construction rather than by remembering.
 *
 * The five placeholder story relics in itemsStoryRelics.js are untouched and
 * remain unreachable until L4's T0 adds raidTierData[4]. They are that layer's
 * business; keys 8-10 are where the two paths meet.
 *
 * ORDER MATTERS. The fragments build an argument, so the next key a faction
 * recovers is always its LOWEST unrecovered one — the roll decides *whether*,
 * never *which*. Tier is a ceiling rather than a bucket (`tier <= raidTier`),
 * so a player who out-levels T1 can still recover D1; nobody is locked out of
 * the first page of their own story.
 *
 * No browser dependencies — the simulator runs this too.
 *
 * See docs/story/StoryAlignment.md §Where the Collapse Keys sit in the ladder.
 */

import * as config from '../constants.js';
import { gameState } from './gameState-core.js';

/**
 * The account's key record, created on demand.
 *
 * Lazy rather than assumed: saves written before this shipped have a `player`
 * without the field, and the load path restores that object verbatim.
 */
export function ensureCollapseKeyState() {
    const player = gameState.player || (gameState.player = {});
    if (!Array.isArray(player.collapseKeys)) player.collapseKeys = [];
    return player.collapseKeys;
}

/** Every authored key, in faction then index order. */
export function allCollapseKeys() {
    return config.collapseKeys || [];
}

/** One key definition by id, or null. */
export function getCollapseKey(id) {
    return allCollapseKeys().find(k => k.id === id) || null;
}

/** Has the account recovered this key? */
export function hasCollapseKey(id) {
    return ensureCollapseKeyState().includes(id);
}

/** The authored keys for one faction, in narrative order. */
export function keysForFaction(faction) {
    return allCollapseKeys()
        .filter(k => k.faction === faction)
        .sort((a, b) => a.index - b.index);
}

/**
 * The next key this faction could recover at this raid tier, or null when the
 * set is complete or the tier is too low for anything still missing.
 *
 * Tier is a CEILING: a T3 raid can yield a missing T1 key, and it will yield
 * that one first, because the story is read in order.
 */
export function nextCollapseKey(faction, tier) {
    if (!faction) return null;
    const owned = ensureCollapseKeyState();
    return keysForFaction(faction).find(k => k.tier <= tier && !owned.includes(k.id)) || null;
}

/**
 * Record a key. Idempotent — returns the definition on a first recovery and
 * null when the account already held it, so callers can use the return value
 * to decide whether to announce anything.
 */
export function recordCollapseKeyCore(id) {
    const key = getCollapseKey(id);
    if (!key) return null;
    const owned = ensureCollapseKeyState();
    if (owned.includes(id)) return null;
    owned.push(id);
    return key;
}

/**
 * Roll for a key at the end of a successful raid.
 *
 * @param {string} faction - The raiding character's faction
 * @param {number} tier - Raid tier
 * @param {Function} rng - Injectable for tests and the simulator
 * @returns {Object|null} The recovered key definition, or null
 */
export function rollCollapseKeyCore(faction, tier, rng = Math.random) {
    const candidate = nextCollapseKey(faction, tier);
    if (!candidate) return null;

    // Direct read, no `?? 0` fallback: a silent 0 would disable the whole story
    // track and look exactly like a player who has not found a key yet.
    // configValidation.js makes a missing value a loud boot failure instead.
    if (rng() >= config.progressionScaling.collapseKeyDropChance) return null;

    return recordCollapseKeyCore(candidate.id);
}

/**
 * Per-faction completion, for the Archive header and its locked slots.
 * `total` counts only the authored keys, so it becomes 10 on its own the day
 * L4 adds keys 8-10 to the data file.
 */
export function collapseKeyProgress() {
    const owned = ensureCollapseKeyState();
    const byFaction = {};
    Object.keys(config.factionSkills || {}).forEach(faction => {
        const keys = keysForFaction(faction);
        byFaction[faction] = {
            owned: keys.filter(k => owned.includes(k.id)).length,
            total: keys.length,
        };
    });
    return byFaction;
}

/** Total keys recovered across all factions. */
export function collapseKeyCount() {
    return ensureCollapseKeyState().length;
}
